import { useEffect, useMemo, useRef, useState } from "react";

import { HospitalResultModal } from "./HospitalResultModal";
import type { HospitalResultRow } from "./types";

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function previewText(items: string[]): string {
  if (!items.length) return "—";
  return items[0] + (items.length > 1 ? ` +${items.length - 1}` : "");
}

function makeInitialColW(cols: number): number[] {
  const base = [170, 220, 180, 150, 140, 160, 140, 240];
  if (cols === base.length) return [...base];
  const w = Math.max(110, Math.floor(1280 / cols));
  return Array.from({ length: cols }, () => w);
}

export function HospitalResultsTable({ rows }: { rows: HospitalResultRow[] }) {
  const [activeRow, setActiveRow] = useState<HospitalResultRow | null>(null);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const wrapRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);

  const columns = useMemo(
    () => [
      { key: "discharge_date", label: "Дата выписки", width: 170 },
      { key: "department", label: "Отделение", width: 220 },
      { key: "doctor", label: "Врач", width: 180 },
      { key: "patient_iin", label: "ИИН пациента", width: 150 },
      { key: "diagnosis", label: "Диагноз", width: 140 },
      { key: "hospital_type", label: "Тип стационара", width: 150 },
      { key: "defect_codes", label: "Код дефекта", width: 140 },
      { key: "defect_hints", label: "Подсказка", width: 1 },
    ],
    [],
  );

  const [colW, setColW] = useState<number[]>(() => makeInitialColW(columns.length));

  useEffect(() => {
    setColW((prev) => (prev.length === columns.length ? prev : makeInitialColW(columns.length)));
  }, [columns.length]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      if (w <= 0) return;
      setContainerW(w);
      setColW((prev) => {
        if (!prev.length) return prev;
        const sum = prev.reduce((acc, cur) => acc + cur, 0);
        if (Math.abs(sum - w) <= 2) return prev;
        const k = w / sum;
        const next = prev.map((px) => Math.max(Math.round(px * k), 110));
        const diff = w - next.reduce((acc, cur) => acc + cur, 0);
        next[0] += diff;
        return next;
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const startResize = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const nextIndex = index + 1;
    if (nextIndex >= colW.length) return;

    const minFor = (idx: number) => (idx === 0 ? 150 : idx === 4 ? 130 : idx === 5 ? 140 : idx === 6 ? 120 : 110);
    const startX = e.clientX;
    const init = [...colW];
    const minLeft = minFor(index);
    const minRight = minFor(nextIndex);

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      let left = init[index] + dx;
      let right = init[nextIndex] - dx;
      if (left < minLeft) {
        right -= minLeft - left;
        left = minLeft;
      }
      if (right < minRight) {
        left -= minRight - right;
        right = minRight;
      }
      const next = [...init];
      next[index] = left;
      next[nextIndex] = right;
      const sum = next.reduce((acc, cur) => acc + cur, 0);
      if (containerW > 0 && Math.abs(sum - containerW) > 2) {
        next[0] += containerW - sum;
      }
      setColW(next);
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const gridTemplate = useMemo(
    () => colW.map((px) => `${px}px`).join(" "),
    [colW],
  );

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;

    const normalize = (value: unknown) => String(value ?? "").trim().toLowerCase();
    const getValue = (row: HospitalResultRow): string | number => {
      switch (sortKey) {
        case "discharge_date":
          return row.discharge_date ? new Date(row.discharge_date).getTime() : 0;
        case "department":
          return normalize(row.department);
        case "doctor":
          return normalize(row.doctor);
        case "patient_iin":
          return normalize(row.patient_iin);
        case "diagnosis":
          return normalize(row.diagnosis);
        case "hospital_type":
          return normalize(row.hospital_type);
        case "defect_codes":
          return normalize(row.defect_codes[0] || "");
        case "defect_hints":
          return normalize(row.defect_hints[0] || "");
        default:
          return "";
      }
    };

    return [...rows].sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const cmp = String(va).localeCompare(String(vb), "ru", { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key: string) => {
    setSortKey((prev) => {
      if (prev !== key) {
        setSortDir("asc");
        return key;
      }
      if (sortDir === "asc") {
        setSortDir("desc");
        return key;
      }
      setSortDir("asc");
      return null;
    });
  };

  return (
    <>
      <div ref={wrapRef} className="hospital-table-wrap">
        <div className="hospital-table-head" style={{ gridTemplateColumns: gridTemplate }}>
          {columns.map((col, idx) => {
            const isLast = idx === columns.length - 1;
            const isSorted = sortKey === col.key;
            return (
              <div
                key={col.key}
                className={`hospital-table-th${isSorted ? " sorted" : ""}`}
                onClick={() => toggleSort(col.key)}
              >
                <span className="hospital-table-th-label">{col.label}</span>
                <span className="hospital-table-sort-mark">
                  {isSorted ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </span>
                {!isLast && (
                  <div
                    className="hospital-table-resize"
                    onMouseDown={(e) => startResize(idx, e)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="hospital-table-body">
          {sortedRows.map((row) => (
            <div
              key={row.request_id}
              className="hospital-table-row"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              <button type="button" className="hospital-table-cell" onClick={() => setActiveRow(row)} title={formatDate(row.discharge_date)}>
                {formatDate(row.discharge_date)}
              </button>
              <button type="button" className="hospital-table-cell" onClick={() => setActiveRow(row)} title={row.department || "—"}>
                {row.department || "—"}
              </button>
              <button type="button" className="hospital-table-cell" onClick={() => setActiveRow(row)} title={row.doctor || "—"}>
                {row.doctor || "—"}
              </button>
              <button type="button" className="hospital-table-cell" onClick={() => setActiveRow(row)} title={row.patient_iin || "—"}>
                {row.patient_iin || "—"}
              </button>
              <button type="button" className="hospital-table-cell" onClick={() => setActiveRow(row)} title={row.diagnosis || "—"}>
                {row.diagnosis || "—"}
              </button>
              <button type="button" className="hospital-table-cell" onClick={() => setActiveRow(row)} title={row.hospital_type || "—"}>
                {row.hospital_type || "—"}
              </button>
              <button type="button" className="hospital-table-cell" onClick={() => setActiveRow(row)} title={previewText(row.defect_codes)}>
                {previewText(row.defect_codes)}
              </button>
              <button type="button" className="hospital-table-cell" onClick={() => setActiveRow(row)} title={previewText(row.defect_hints)}>
                {previewText(row.defect_hints)}
              </button>
            </div>
          ))}
        </div>
      </div>

      {activeRow ? <HospitalResultModal row={activeRow} onClose={() => setActiveRow(null)} /> : null}

      <style>{`
        .hospital-table-wrap {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          background: #fff;
          height: calc(100vh - 220px);
          min-height: 520px;
          display: flex;
          flex-direction: column;
        }
        .hospital-table-head,
        .hospital-table-row {
          display: grid;
          grid-auto-flow: column;
          align-items: stretch;
        }
        .hospital-table-head {
          background: #00b8ba;
          color: #fff;
          min-height: 72px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          user-select: none;
        }
        .hospital-table-th,
        .hospital-table-cell {
          padding: 10px 12px;
          border-right: 1px solid rgba(255,255,255,0.16);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .hospital-table-th {
          position: relative;
          display: flex;
          align-items: flex-start;
          gap: 6px;
          padding-right: 18px;
          cursor: pointer;
        }
        .hospital-table-th.sorted {
          background: #48d1cc;
        }
        .hospital-table-th-label {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.35;
          white-space: normal;
        }
        .hospital-table-sort-mark {
          margin-left: auto;
          padding-top: 2px;
          font-size: 12px;
          line-height: 1;
          flex: 0 0 auto;
        }
        .hospital-table-resize {
          position: absolute;
          right: 0;
          top: 0;
          width: 8px;
          height: 100%;
          cursor: col-resize;
          user-select: none;
          touch-action: none;
        }
        .hospital-table-body {
          flex: 1 1 auto;
          overflow: auto;
        }
        .hospital-table-row {
          min-height: 56px;
          border-top: 1px solid #eef2f7;
          background: #fff;
        }
        .hospital-table-cell {
          appearance: none;
          border: 0;
          background: transparent;
          text-align: left;
          cursor: pointer;
          color: #0f172a;
          font-size: 13px;
        }
        .hospital-table-row:hover {
          background: #f8fafc;
        }
      `}</style>
    </>
  );
}
